import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActivityStats } from '../../api';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import {
  ChartBarIcon,
  UsersIcon,
  ClockIcon,
  SignalIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

export default function ActivityPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const res = await getActivityStats();
      setStats(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (min) => {
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const minutesAgo = (iso) => {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diff < 1) return 'just now';
    if (diff === 1) return '1 min ago';
    return `${diff} min ago`;
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Activity Stats</h2>
          <div className="subtitle">Monitor user activity and login statistics</div>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {stats && (
        <>
          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div className="card" style={{ textAlign: 'center', padding: 20 }}>
              <SignalIcon style={{ width: 32, height: 32, color: 'var(--success)', margin: '0 auto 8px' }} />
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success)' }}>{stats.activeNow.length}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Currently Active</div>
            </div>
            <div className="card" style={{ textAlign: 'center', padding: 20 }}>
              <UsersIcon style={{ width: 32, height: 32, color: 'var(--info)', margin: '0 auto 8px' }} />
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--info)' }}>{stats.todayLogins}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Logins Today</div>
            </div>
            <div className="card" style={{ textAlign: 'center', padding: 20 }}>
              <UsersIcon style={{ width: 32, height: 32, color: 'var(--accent)', margin: '0 auto 8px' }} />
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)' }}>{stats.totalUsers}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Total Users</div>
            </div>
          </div>

          {/* Currently active users */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SignalIcon className="icon" style={{ color: 'var(--success)' }} /> Currently Active Users
              </span>
            </div>
            {stats.activeNow.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', padding: 16, textAlign: 'center' }}>No active users right now</div>
            ) : (
              <div className="members-list">
                {stats.activeNow.map(u => (
                  <div key={u.userId} className="member-item">
                    <div className="member-info">
                      <div className="member-avatar" style={{ background: 'var(--success)', fontSize: 14 }}>
                        {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : u.displayName?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div className="member-name">{u.displayName}</div>
                        <div className="member-role">@{u.username}</div>
                        {(u.organizations || []).length > 0 && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                            {u.organizations.map(o => (
                              <span
                                key={o._id}
                                onClick={() => navigate(`/org/${o._id}`)}
                                style={{ fontSize: 11, padding: '2px 8px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}
                              >
                                {o.logo ? (
                                  <img src={o.logo} alt="" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <BuildingOfficeIcon style={{ width: 12, height: 12 }} />
                                )}
                                {o.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--info)', fontWeight: 600 }}>
                        <ClockIcon style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 2 }} />
                        {formatDuration(u.durationMin)}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{minutesAgo(u.lastActive)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Daily login chart (simple bar chart) */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ChartBarIcon className="icon" /> Daily Logins (Last 30 Days)
              </span>
            </div>
            {stats.dailyStats.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', padding: 16, textAlign: 'center' }}>No data yet</div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 160, padding: '16px 8px 0' }}>
                {stats.dailyStats.map(d => {
                  const maxLogins = Math.max(...stats.dailyStats.map(x => x.logins), 1);
                  const height = Math.max((d.logins / maxLogins) * 120, 4);
                  return (
                    <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{d.logins}</span>
                      <div style={{
                        width: '100%', maxWidth: 24, height, background: 'var(--accent)',
                        borderRadius: '4px 4px 0 0', minWidth: 6
                      }} title={`${d.date}: ${d.logins} logins`} />
                    </div>
                  );
                })}
              </div>
            )}
            {stats.dailyStats.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px 12px', fontSize: 10, color: 'var(--text-muted)' }}>
                <span>{stats.dailyStats[0]?.date}</span>
                <span>{stats.dailyStats[stats.dailyStats.length - 1]?.date}</span>
              </div>
            )}
          </div>

          {/* Top active users */}
          <div className="card">
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <UsersIcon className="icon" /> Most Active Users (Last 30 Days)
              </span>
            </div>
            {stats.topUsers.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', padding: 16, textAlign: 'center' }}>No data yet</div>
            ) : (
              <div className="members-list">
                {[...stats.topUsers]
                  .sort((a, b) => b.activeDays - a.activeDays || b.totalMinutes - a.totalMinutes)
                  .map((u, i) => (
                  <div key={u.userId} className="member-item">
                    <div className="member-info">
                      <div className="member-avatar" style={{
                        background: i === 0 ? 'var(--warning)' : i === 1 ? 'var(--text-secondary)' : i === 2 ? '#cd7f32' : 'var(--accent)',
                        fontSize: 14
                      }}>
                        {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (i < 3 ? (i + 1) : u.displayName?.charAt(0)?.toUpperCase())}
                      </div>
                      <div>
                        <div className="member-name">{u.displayName}</div>
                        <div className="member-role">@{u.username}</div>
                        {(u.organizations || []).length > 0 && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                            {u.organizations.map(o => (
                              <span
                                key={o._id}
                                onClick={() => navigate(`/org/${o._id}`)}
                                style={{ fontSize: 11, padding: '2px 8px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}
                              >
                                {o.logo ? (
                                  <img src={o.logo} alt="" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <BuildingOfficeIcon style={{ width: 12, height: 12 }} />
                                )}
                                {o.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <span className="badge badge-info">{u.activeDays} days</span>
                      {u.totalMinutes > 0 && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 2 }}>
                          <ClockIcon style={{ width: 11, height: 11 }} />{formatDuration(u.totalMinutes)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
